import type { Grade, LevelConfig, Tile } from './types.js';

/**
 * The client↔server contract.
 *
 * Both halves import these types, so a renamed field or a changed payload is a
 * compile error rather than a silent runtime mismatch during a lesson.
 */

export type SessionMode = 'lockstep' | 'race';

export type SessionPhase =
  | 'lobby'          // players joining, host has not started
  | 'countdown'      // 3… 2… 1…
  | 'round_active'
  | 'round_paused'
  | 'round_results'
  | 'ended';

/** One round of a session: a frozen config plus the seed every board came from. */
export interface RoundPlan {
  grade: Grade;
  level: number;
  /** Teacher overrides already folded in — the round is decided before it runs. */
  config: LevelConfig;
  /** Set when the round came from a teacher's own curriculum rather than the catalogue. */
  stageId?: string;
  levelId?: string;
  /** What to call this round on the projector. Empty falls back to the level name. */
  title?: string;
}

export interface RoundInfo {
  index: number;
  total: number;
  seed: number;
  grade: Grade;
  level: number;
  config: LevelConfig;
  /** Teacher's own name for this exercise, when the round came from a curriculum. */
  title?: string;
  /** Epoch ms. Null while the round has not started. */
  startedAt: number | null;
  /** Epoch ms the clock expires, or null for an untimed round. */
  endsAt: number | null;
}

/** What every player and the projector may see about a player. */
export interface PublicPlayer {
  id: string;
  nickname: string;
  score: number;
  roundScore: number;
  /** 0–1: how much of their board is cleared. Drives the race bars. */
  progress: number;
  cleared: boolean;
  connected: boolean;
  badges: string[];
  rank: number;
}

export interface SessionSnapshot {
  code: string;
  className: string;
  /** Credited on the projector, the join screen and the results table. */
  teacherName: string;
  /**
   * Asset ids of this teacher's encouragement recordings, if they uploaded any.
   *
   * Sent with the session rather than fetched: every device in the room needs
   * them the moment the first answer lands, and they are the same for everyone.
   * Empty means the synthesised praise is used, which is the default.
   */
  praiseClips: string[];
  mode: SessionMode;
  phase: SessionPhase;
  round: RoundInfo | null;
  players: PublicPlayer[];
  /** Seconds remaining on the round clock, or null when untimed. */
  secondsLeft: number | null;
}

/** A player's own view of their board. Tiles are the server's, never the client's. */
export interface PlayerBoardState {
  tiles: Tile[];
  rows: number;
  cols: number;
  score: number;
  combo: number;
  addsLeft: number;
  hintsLeft: number;
  matches: number;
  misses: number;
  /**
   * Tiles still to be found.
   *
   * On a pair board this is every uncleared tile; on a hunt board only the ones
   * that answer the question, since the decoys are meant to stay put.
   */
  targetsLeft: number;
}

export interface TapResult {
  ok: boolean;
  /** Why a tap was refused, for the equation line. */
  reason?: 'rule' | 'connection' | 'stale' | 'phase';
  points: number;
  combo: number;
  score: number;
  /** Ids cleared by this tap. Empty on a miss. */
  clearedIds: number[];
  /** Present only when the board's shape changed (row collapse or refill). */
  board?: PlayerBoardState;
  /** Tiles still to be found, so both verdicts answer the same question. */
  targetsLeft: number;
  cleared: boolean;
  /** The board was stranded after this match and re-dealt; `board` carries it. */
  reshuffled?: boolean;
}

/**
 * The verdict on a single-tile tap in a hunt round.
 *
 * Deliberately the same shape as `TapResult` minus the second tile, so the
 * client's effect layer and the server's scoring path do not fork.
 */
export interface PickResult {
  ok: boolean;
  reason?: 'rule' | 'stale' | 'phase';
  points: number;
  combo: number;
  score: number;
  /** The id cleared by this tap. Empty when the tap was wrong. */
  clearedIds: number[];
  targetsLeft: number;
  cleared: boolean;
}

/** What a student is told about a class before they type their name. */
export interface ClassInfo {
  code: string;
  className: string;
  teacherName: string;
  live: boolean;
}

export interface JoinResult {
  ok: boolean;
  error?: 'no_session' | 'bad_pin' | 'name_taken' | 'session_ended' | 'invalid';
  /** Opaque resume token, stored by the player's browser. */
  token?: string;
  playerId?: string;
  snapshot?: SessionSnapshot;
  board?: PlayerBoardState;
}

export interface RoundResultRow {
  playerId: string;
  nickname: string;
  roundScore: number;
  totalScore: number;
  matches: number;
  misses: number;
  maxCombo: number;
  cleared: boolean;
  rank: number;
  /** Codes newly unlocked by this round. */
  newBadges: string[];
}

/* ── event names ───────────────────────────────────────────────────────────
   String constants rather than inline literals: one typo in one place then
   fails the build instead of silently never firing.
   ────────────────────────────────────────────────────────────────────────── */
export const EV = {
  // player → server
  join: 'player:join',
  resume: 'player:resume',
  tap: 'player:tap',
  pick: 'player:pick',
  add: 'player:add',
  hint: 'player:hint',
  leave: 'player:leave',
  // host → server
  hostAttach: 'host:attach',
  hostStart: 'host:start',
  hostNext: 'host:next',
  hostSkip: 'host:skip',
  hostPause: 'host:pause',
  hostResume: 'host:resume',
  hostEnd: 'host:end',
  hostKick: 'host:kick',
  // server → everyone
  state: 'session:state',
  board: 'session:board',
  roundStart: 'round:start',
  roundEnd: 'round:end',
  badges: 'achievement:unlocked',
  toast: 'session:toast',
} as const;

export interface JoinPayload { code: string; nickname: string; pin: string }
export interface ResumePayload { token: string }
export interface TapPayload { a: number; b: number }
export interface PickPayload { i: number }
export interface HostAttachPayload { code: string }
export interface KickPayload { playerId: string }
/** `where` is a pair on a pair board and a single index on a hunt board. */
export interface HintResult { ok: boolean; where?: number[]; hintsLeft: number }
export interface AddResult { ok: boolean; board?: PlayerBoardState; addsLeft: number }

/** Class codes avoid 0/O and 1/I — they are read aloud off a projector. */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 6;

export function isValidCode(code: string): boolean {
  return new RegExp(`^[${CODE_ALPHABET}]{${CODE_LENGTH}}$`).test(code.toUpperCase());
}

export function isValidNickname(name: string): boolean {
  const n = name.trim();
  return n.length >= 2 && n.length <= 18;
}

export function isValidPin(pin: string): boolean {
  return /^\d{4}$/.test(pin);
}
